import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, FormControl, HStack, ModalBody, ModalFooter, ModalHeader, VStack } from '@chakra-ui/react';
import { MdCancel, MdCheck } from 'react-icons/md';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE } from '@constants';
import DynamicFormField from '@components/DynamicFormField/DynamicFormField';


const ActionButton = ({ onClick, isLoading, isDisabled, colorScheme, leftIcon, children }) => (
  <Button
    variant={'outline'}
    colorScheme={colorScheme}
    size={'sm'}
    border={'2px solid'}
    borderRadius={'xl'}
    isDisabled={isDisabled}
    isLoading={isLoading}
    onClick={onClick}
    leftIcon={leftIcon}
  >
    {children}
  </Button>
);


const DynamicInputModal = ({
  title,
  onClose,
  onSubmit,
  fields,
  isLoading,
  onFieldChange,
  // Optional render-prop for extra UI rendered under the fields, inside the
  // modal body. Receives the live formData so callers can build summaries /
  // previews. Example: live "transfer summary" card in Create Transfer.
  renderFooter
}) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initialData = fields.reduce((acc, field) => {
      // Only include field value if it's actually defined
      if (field.value !== undefined && field.value !== null) {
        acc[field.name] = field.value;
      }
      return acc;
    }, {});

    if (!isInitialized) {
      // First initialization - set all values
      setFormData(initialData);
      setIsInitialized(true);
    } else {
      // Subsequent updates:
      //  - Fields with `isEditable: false` AND a `value` in the latest config
      //    are read-only mirrors of derived state (e.g. currency, account id);
      //    always sync them to the latest value.
      //  - Editable fields with a `value` only seed once when empty so we don't
      //    clobber what the user has typed.
      setFormData((prev) => {
        const updated = { ...prev };
        fields.forEach((field) => {
          const hasValue = field.value !== undefined && field.value !== null;
          if (!hasValue) return;
          if (field.isEditable === false) {
            updated[field.name] = field.value;
          } else if (
            prev[field.name] === undefined ||
            prev[field.name] === null ||
            prev[field.name] === ''
          ) {
            updated[field.name] = field.value;
          }
        });
        return updated;
      });
    }
  }, [fields]);

  const handleInputChange = ({ target: { name, value, type, checked } }) => {
    const newValue = type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Notify parent component of field change if callback provided
    if (onFieldChange) {
      onFieldChange(name, newValue);
    }
  };

  const handleFormSubmit = async () => {
    const isValid = fields.every(field => {
      if (field.isRequired) {
        return formData[field.name] !== undefined && formData[field.name] !== null && formData[field.name] !== '';
      }
      return true;
    });

    if (!isValid) {
      dispatch(
        setAlert({
          title: 'Please fill all the required fields',
          status: ALERT_STATUS_TYPE.INFO
        })
      );
      return;
    }

    await onSubmit(formData);
    onClose();
  };

  return (
    <>
      <ModalHeader>{title}</ModalHeader>
      <FormControl onSubmit={handleFormSubmit}>
        <ModalBody maxH="65vh" overflowY="auto" className="scroll-md">
          <VStack alignItems={'stretch'} w={'full'}>
            {fields?.map((field, index) => (
              <DynamicFormField
                key={index}
                fieldConfig={field}
                data={formData}
                handleChange={handleInputChange}
              />
            ))}
          </VStack>
          {/*
           * renderFooter is rendered INSIDE the scrollable ModalBody so the
           * Beneficiary Details panel (and any other footer content) scrolls
           * with the form instead of overflowing the modal height.
           */}
          {typeof renderFooter === 'function' && (
            <Box pt={2} pb={1}>
              {renderFooter(formData)}
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack>
            <ActionButton
              onClick={onClose}
              isDisabled={isLoading}
              isLoading={false}
              colorScheme="red"
              leftIcon={<MdCancel />}
            >
              Cancel
            </ActionButton>
            <ActionButton
              onClick={handleFormSubmit}
              isLoading={isLoading}
              isDisabled={isLoading}
              colorScheme="brand"
              leftIcon={<MdCheck />}
            >
              Submit
            </ActionButton>
          </HStack>
        </ModalFooter>
      </FormControl>
    </>
  );
};

export default DynamicInputModal;